import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import EnvelopeLoader from "@/components/EnvelopeLoader.jsx";
import SecretLetter from "@/components/SecretLetter.jsx";
import EarthTransition from "@/components/EarthTransition.jsx";
import VarmalaScroll from "@/components/VarmalaScroll.jsx";
import Alliance from "@/components/Alliance.jsx";
import Families from "@/components/Families.jsx";
import Countdown from "@/components/Countdown.jsx";
import Nav from "@/components/Nav.jsx";
import OurStory from "@/components/OurStory.jsx";
import Itinerary from "@/components/Itinerary.jsx";
import Venue from "@/components/Venue.jsx";
import OrnamentDivider from "@/components/OrnamentDivider.jsx";
import RSVP from "@/components/RSVP.jsx";
import FooterBlock from "@/components/Footer.jsx";
import FilmGrain from "@/components/FilmGrain.jsx";

import { useLenis } from "@/hooks/useLenis.js";
import { ScrollTrigger } from "@/lib/gsap";

/**
 * Top-level state machine for the site.
 *
 *   envelope → earth → scroll
 *
 * We render all three scenes. `EnvelopeLoader` and `EarthTransition` are
 * fixed-position overlays that animate themselves in/out; the main scroll
 * content lives beneath them and is hidden (opacity 0 + body locked)
 * until the state reaches "scroll".
 */
export default function App() {
  // the-secret-letter branch: open on the letter first
  const [phase, setPhase] = useState("letter"); // "letter" | "envelope" | "earth" | "scroll"

  // Smooth scroll — only enabled once the user has reached the scroll phase.
  useLenis(phase === "scroll");

  // Lock body scroll until we're in the scroll phase.
  useEffect(() => {
    if (phase === "scroll") {
      document.body.classList.remove("locked");
      // Refresh ScrollTrigger so its start/end calculations account for content
      // that only just became visible.
      ScrollTrigger.refresh();
    } else {
      document.body.classList.add("locked");
    }
  }, [phase]);

  const handleEnvelopeOpen = useCallback(() => {
    setPhase("earth");
  }, []);

  const handleEarthDone = useCallback(() => {
    setPhase("scroll");
  }, []);

  return (
    <>
      <FilmGrain />

      <AnimatePresence mode="wait">
        {phase === "letter" && (
          <motion.div
            key="letter"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className="fixed inset-0 z-[600] overflow-y-auto"
          >
            <SecretLetter />
          </motion.div>
        )}

        {phase === "envelope" && (
          <motion.div
            key="envelope"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="fixed inset-0 z-[500]"
          >
            <EnvelopeLoader onOpen={handleEnvelopeOpen} />
          </motion.div>
        )}

        {phase === "earth" && (
          <motion.div
            key="earth"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-[400]"
          >
            <EarthTransition onComplete={handleEarthDone} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main site — hidden until we reach the scroll phase */}
      <motion.main
        id="main-content"
        initial={false}
        animate={{ opacity: phase === "scroll" ? 1 : 0 }}
        transition={{ duration: 0.5, delay: phase === "scroll" ? 0.1 : 0 }}
        className="relative"
      >
        <Nav />
        <VarmalaScroll />
        <Alliance />
        <OrnamentDivider />
        <OurStory />
        <OrnamentDivider />
        <Itinerary />
        <OrnamentDivider />
        <Venue />
        <OrnamentDivider />
        <RSVP />
        <Families />
        <Countdown />
        <FooterBlock />
      </motion.main>

    </>
  );
}
